// -- Constants
const DESKTOP_QUERY = '(min-width: 750px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const IMAGE_ITEM_DURATION_MS = 5000;
const MINIMUM_TIMER_MS = 100;

class DarkrimBannerSlider extends HTMLElement {
  // -- State and variables
  #activeSlide = null;
  #autoplayDeadline = 0;
  #autoplayTimer = null;
  #desktopMediaQuery = window.matchMedia(DESKTOP_QUERY);
  #metadataController = null;
  #pausedByHover = false;
  #reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
  #remainingItemDuration = null;
  #slideshow = null;

  // -- Derived values
  get #autoplayEnabled() {
    return (
      this.dataset.autoplay === 'true' &&
      (this.#slideshow?.slides?.length || 0) > 1 &&
      !this.#reducedMotionQuery.matches
    );
  }

  get #currentViewport() {
    return this.#desktopMediaQuery.matches ? 'pc' : 'mobile';
  }

  get #playbackPaused() {
    return document.hidden || this.#pausedByHover || this.#reducedMotionQuery.matches;
  }

  // -- Functions
  #findActiveSlide() {
    const currentSlide = this.#slideshow?.slides?.[this.#slideshow.current];
    if (currentSlide instanceof HTMLElement) return currentSlide;

    return this.querySelector("slideshow-slide[aria-hidden='false']");
  }

  #syncActiveSlide(slide = this.#findActiveSlide(), restartVideo = true) {
    if (!(slide instanceof HTMLElement)) return;

    this.#metadataController?.abort();
    this.#metadataController = new AbortController();
    this.#clearAutoplayTimer();
    this.#releaseInactiveViewportVideos();
    this.#pauseInactiveVideos(slide);
    this.#activeSlide = slide;

    const banner = slide.querySelector('.darkrim-banner');
    const mediaMode = banner?.dataset[`${this.#currentViewport}MediaMode`];
    const video = mediaMode === 'video' ? this.#hydrateVideo(slide) : null;

    if (video instanceof HTMLVideoElement) {
      if (restartVideo) {
        try {
          video.currentTime = 0;
        } catch {
          // The browser will start at zero once video metadata becomes available.
        }
      }

      if (this.#playbackPaused) {
        video.pause();
      } else {
        this.#playVideo(video);
      }
    }

    this.#scheduleActiveItem(video);
  }

  #hydrateVideo(slide) {
    const host = slide.querySelector('[data-darkrim-banner-video-host]');
    if (!(host instanceof HTMLElement)) return null;

    const viewport = this.#currentViewport;
    const existingVideo = host.querySelector('video');
    if (host.dataset.viewport === viewport && existingVideo instanceof HTMLVideoElement) {
      return existingVideo;
    }

    const template = slide.querySelector(`[data-darkrim-banner-video-template="${viewport}"]`);
    if (!(template instanceof HTMLTemplateElement)) return null;

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return null;

    video.setAttribute('aria-hidden', 'true');
    video.tabIndex = -1;
    video.muted = true;
    video.playsInline = true;

    host.replaceChildren(fragment);
    host.dataset.viewport = viewport;
    return video;
  }

  #releaseInactiveViewportVideos() {
    const viewport = this.#currentViewport;

    this.querySelectorAll('[data-darkrim-banner-video-host]').forEach((host) => {
      if (!(host instanceof HTMLElement) || !host.dataset.viewport || host.dataset.viewport === viewport) return;

      host.querySelector('video')?.pause();
      host.replaceChildren();
      delete host.dataset.viewport;
    });
  }

  #pauseInactiveVideos(activeSlide) {
    this.querySelectorAll('slideshow-slide video').forEach((video) => {
      if (!(video instanceof HTMLVideoElement) || video.closest('slideshow-slide') === activeSlide) return;

      video.pause();
      try {
        video.currentTime = 0;
      } catch {
        // Metadata is not guaranteed to be available for a previously hidden video.
      }
    });
  }

  #scheduleActiveItem(video) {
    this.#clearAutoplayTimer();
    if (!this.#autoplayEnabled || this.#playbackPaused || this.#isAtNonLoopingEnd()) return;

    if (!(video instanceof HTMLVideoElement)) {
      this.#setAutoplayTimer(IMAGE_ITEM_DURATION_MS);
      return;
    }

    // Five seconds is the safe fallback if metadata cannot be read; metadata replaces it with the real duration.
    this.#setAutoplayTimer(IMAGE_ITEM_DURATION_MS);

    const applyVideoDuration = () => {
      if (video !== this.#getActiveVideo()) return;

      const remainingDuration = this.#getRemainingVideoDuration(video);
      if (remainingDuration === null) return;

      if (this.#playbackPaused) {
        this.#clearAutoplayTimer();
        this.#remainingItemDuration = remainingDuration;
        return;
      }

      this.#setAutoplayTimer(remainingDuration);
    };

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      applyVideoDuration();
    } else {
      video.addEventListener('loadedmetadata', applyVideoDuration, {
        once: true,
        signal: this.#metadataController?.signal,
      });
    }
  }

  #getActiveVideo() {
    return this.#activeSlide?.querySelector('[data-darkrim-banner-video-host] video') || null;
  }

  #getRemainingVideoDuration(video) {
    if (!Number.isFinite(video.duration) || video.duration <= 0) return null;

    const currentTime = Number.isFinite(video.currentTime) ? video.currentTime : 0;
    const remainingSeconds = Math.max(video.duration - currentTime, 0);
    return Math.max(remainingSeconds * 1000, MINIMUM_TIMER_MS);
  }

  #setAutoplayTimer(duration) {
    this.#clearAutoplayTimer();

    const safeDuration = Math.max(Number(duration) || IMAGE_ITEM_DURATION_MS, MINIMUM_TIMER_MS);
    this.#autoplayDeadline = performance.now() + safeDuration;
    this.#autoplayTimer = window.setTimeout(this.#advance, safeDuration);
  }

  #clearAutoplayTimer(preserveRemaining = false) {
    if (preserveRemaining && this.#autoplayTimer !== null) {
      this.#remainingItemDuration = Math.max(this.#autoplayDeadline - performance.now(), MINIMUM_TIMER_MS);
    } else if (!preserveRemaining) {
      this.#remainingItemDuration = null;
    }

    if (this.#autoplayTimer !== null) window.clearTimeout(this.#autoplayTimer);
    this.#autoplayTimer = null;
    this.#autoplayDeadline = 0;
  }

  #isAtNonLoopingEnd() {
    return Boolean(this.#slideshow && !this.#slideshow.infinite && this.#slideshow.atEnd);
  }

  #playVideo(video) {
    video.play().catch(() => {
      // Autoplay can be denied by browser policy; the video poster remains visible.
    });
  }

  #pausePlayback() {
    this.#clearAutoplayTimer(true);
    this.#getActiveVideo()?.pause();
  }

  #resumePlayback() {
    if (this.#playbackPaused) return;

    const video = this.#getActiveVideo();
    if (video instanceof HTMLVideoElement) this.#playVideo(video);
    if (!this.#autoplayEnabled || this.#isAtNonLoopingEnd()) return;

    if (video instanceof HTMLVideoElement) {
      this.#scheduleActiveItem(video);
      return;
    }

    const remainingDuration = this.#remainingItemDuration ?? IMAGE_ITEM_DURATION_MS;
    this.#remainingItemDuration = null;
    this.#setAutoplayTimer(remainingDuration);
  }

  // -- Listeners
  #advance = () => {
    this.#autoplayTimer = null;
    this.#autoplayDeadline = 0;
    this.#remainingItemDuration = null;

    if (!this.#autoplayEnabled || this.#playbackPaused || this.#isAtNonLoopingEnd()) return;
    this.#slideshow?.next();
  };

  #handleSlideSelect = (event) => {
    this.#syncActiveSlide(event.detail?.slide, true);
  };

  #handleViewportChange = () => {
    this.#syncActiveSlide(this.#findActiveSlide(), true);
  };

  #handleMotionPreferenceChange = () => {
    if (this.#reducedMotionQuery.matches) {
      this.#pausePlayback();
      return;
    }

    this.#resumePlayback();
  };

  #handleVisibilityChange = () => {
    document.hidden ? this.#pausePlayback() : this.#resumePlayback();
  };

  #handleMouseEnter = () => {
    if (!this.#autoplayEnabled) return;
    this.#pausedByHover = true;
    this.#pausePlayback();
  };

  #handleMouseLeave = () => {
    if (!this.#pausedByHover) return;
    this.#pausedByHover = false;
    this.#resumePlayback();
  };

  // -- Lifecycle hooks
  async connectedCallback() {
    await customElements.whenDefined('slideshow-component');
    if (!this.isConnected) return;

    this.#slideshow = this.querySelector('slideshow-component');
    if (!this.#slideshow) return;

    this.addEventListener('slideshow:select', this.#handleSlideSelect);
    this.addEventListener('mouseenter', this.#handleMouseEnter);
    this.addEventListener('mouseleave', this.#handleMouseLeave);
    this.#desktopMediaQuery.addEventListener('change', this.#handleViewportChange);
    this.#reducedMotionQuery.addEventListener('change', this.#handleMotionPreferenceChange);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    queueMicrotask(() => this.#syncActiveSlide(this.#findActiveSlide(), true));
  }

  disconnectedCallback() {
    this.#metadataController?.abort();
    this.#clearAutoplayTimer();
    this.#getActiveVideo()?.pause();
    this.removeEventListener('slideshow:select', this.#handleSlideSelect);
    this.removeEventListener('mouseenter', this.#handleMouseEnter);
    this.removeEventListener('mouseleave', this.#handleMouseLeave);
    this.#desktopMediaQuery.removeEventListener('change', this.#handleViewportChange);
    this.#reducedMotionQuery.removeEventListener('change', this.#handleMotionPreferenceChange);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }
}

if (!customElements.get('darkrim-banner-slider')) {
  customElements.define('darkrim-banner-slider', DarkrimBannerSlider);
}
